import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BotAvatarIcon from './icons/BotAvatarIcon';
import UserAvatarIcon from './icons/UserAvatarIcon';
import ThumbsUpIcon from './icons/ThumbsUpIcon';
import ThumbsDownIcon from './icons/ThumbsDownIcon';
import CheckmarkIcon from './icons/CheckmarkIcon';
import formatTimestamp from './utils/formatTimestamp';
import ReactMarkdown from 'react-markdown';

const MessageBubble = ({
  message,
  showTimestamp,
  onRetry,
  onFeedback,
  isStreaming,
  streamingDelay
}) => {
  const isBot = message.role === 'bot';
  const canRetry = Boolean(message.retryPrompt);
  const [displayText, setDisplayText] = useState(isStreaming ? '' : (message.text || ''));
  const displayTextRef = useRef(displayText);

  useEffect(() => {
    displayTextRef.current = displayText;
  }, [displayText]);

  useEffect(() => {
    const nextText = isStreaming ? '' : (message.text || '');
    displayTextRef.current = nextText;
    setDisplayText(nextText);
  }, [message.id]);

  useEffect(() => {
    if (!isStreaming) {
      const fullText = message.text || '';
      displayTextRef.current = fullText;
      setDisplayText(fullText);
      return undefined;
    }

    const targetText = message.text || '';
    if (!targetText) {
      if (displayTextRef.current) {
        displayTextRef.current = '';
        setDisplayText('');
      }
      return undefined;
    }

    if (targetText.length < displayTextRef.current.length) {
      displayTextRef.current = targetText;
      setDisplayText(targetText);
      return undefined;
    }

    let timeoutId = null;
    let isCancelled = false;

    const advanceText = () => {
      if (isCancelled) {
        return;
      }

      const currentLength = displayTextRef.current.length;
      if (currentLength >= targetText.length) {
        return;
      }

      const nextText = targetText.slice(0, currentLength + 1);
      displayTextRef.current = nextText;
      setDisplayText(nextText);

      if (nextText.length < targetText.length) {
        timeoutId = window.setTimeout(advanceText, streamingDelay);
      }
    };

    if (displayTextRef.current !== targetText) {
      timeoutId = window.setTimeout(advanceText, streamingDelay);
    }

    return () => {
      isCancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isStreaming, message.text, streamingDelay]);

  const showTypingDots = message.status === 'loading' && !displayText && !message.text;
  const showStreamingCursor = isStreaming && !!message.text;
  const errorText = message.errorMessage || 'Failed to send message. Retry?';
  const isUpFeedbackLocked = message.feedbackSubmitted && message.feedback === 'up';
  const isDownFeedbackLocked = message.feedbackSubmitted && message.feedback === 'down';

  return (
    <div className={`chatbot-message-row ${isBot ? 'is-bot' : 'is-user'}`}>
      {isBot && (
        <div className="chatbot-avatar chatbot-avatar-bot" aria-label="AI assistant avatar">
          <BotAvatarIcon />
        </div>
      )}
      <div className="chatbot-message-content">
        <div className={`chatbot-bubble ${isBot ? 'bot-bubble' : 'user-bubble'}`}>
          <div className={showStreamingCursor ? 'chatbot-streaming-text' : ''}>
            <div className="chatbot-markdown">
              <ReactMarkdown
                components={{
                  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />
                }}
              >
                {displayText || ''}
              </ReactMarkdown>
            </div>
            {showStreamingCursor && <span className="chatbot-streaming-cursor" aria-hidden="true">|</span>}
          </div>
          {showTypingDots && (
            <div className="chatbot-loading-dots" aria-label="Assistant is typing">
              <span />
              <span />
              <span />
            </div>
          )}
          {message.status === 'error' && (
            <div className="chatbot-error-area">
              <span>{errorText}</span>
              {canRetry && (
                <button type="button" onClick={() => onRetry(message.id)} aria-label="Retry failed response">Retry</button>
              )}
            </div>
          )}
        </div>
        {showTimestamp && <span className="chatbot-timestamp">{formatTimestamp(message.createdAt)}</span>}
        {isBot && message.status !== 'loading' && message.id !== 'm-0' && (
          <div className="chatbot-feedback-controls">
            <button
              type="button"
              aria-label="Mark as helpful"
              onClick={() => onFeedback(message.id, 'up')}
              disabled={isUpFeedbackLocked}
              className={`feedback-btn up ${message.feedback === 'up' ? 'is-selected' : ''}`}
            >
              <ThumbsUpIcon />
            </button>
            <button
              type="button"
              aria-label="Mark as unhelpful"
              onClick={() => onFeedback(message.id, 'down')}
              disabled={isDownFeedbackLocked}
              className={`feedback-btn down ${message.feedback === 'down' ? 'is-selected' : ''}`}
            >
              <ThumbsDownIcon />
            </button>
            {message.feedbackSubmitted && (
              <span className="chatbot-feedback-submitted" aria-label="Feedback submitted">
                <CheckmarkIcon />
              </span>
            )}
          </div>
        )}
      </div>
      {!isBot && (
        <div className="chatbot-avatar chatbot-avatar-user" aria-label="User avatar">
          <UserAvatarIcon />
        </div>
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.oneOf(['bot', 'user']).isRequired,
    text: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['complete', 'loading', 'error']),
    feedback: PropTypes.oneOf(['up', 'down', null]),
    feedbackSubmitted: PropTypes.bool,
    errorMessage: PropTypes.string,
    retryPrompt: PropTypes.string
  }).isRequired,
  showTimestamp: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onFeedback: PropTypes.func.isRequired,
  isStreaming: PropTypes.bool,
  streamingDelay: PropTypes.number
};

MessageBubble.defaultProps = {
  showTimestamp: true,
  isStreaming: false,
  streamingDelay: 50
};

export default memo(MessageBubble);
